import React, { useState } from "react";
import { apiClient, useAuth } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Download, Trash2, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import { OrgSecurityCard } from "./OrgSecurityCard";

export function SecurityTab() {
  const { logout, user } = useAuth();
  const isOrgAdmin = user?.role === "admin" || user?.role === "superuser";
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPw, setChangingPw] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Password strength checker
  const getPasswordStrength = (pw) => {
    if (!pw) return { score: 0, label: "", color: "" };
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[a-z]/.test(pw)) score++;
    if (/\d/.test(pw)) score++;
    if (/[!@#$%^&*(),.?":{}|<>\-_=+\[\]\\;'/`~]/.test(pw)) score++;
    const labels = ["", "Weak", "Fair", "Good", "Strong", "Excellent"];
    const colors = ["", "bg-red-500", "bg-amber-500", "bg-yellow-500", "bg-emerald-500", "bg-emerald-600"];
    return { score, label: labels[score] || "", color: colors[score] || "" };
  };

  const strength = getPasswordStrength(newPassword);

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      toast.error("Please fill in all fields"); return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match"); return;
    }
    setChangingPw(true);
    try {
      await apiClient.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password updated successfully");
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to change password");
    } finally { setChangingPw(false); }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const res = await apiClient.get("/gdpr/export-my-data");
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `contrack_my_data_${new Date().toISOString().split("T")[0]}.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Data exported successfully");
    } catch {
      toast.error("Failed to export data");
    } finally { setExporting(false); }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) { toast.error("Please enter your password to confirm"); return; }
    setDeleting(true);
    try {
      await apiClient.post("/gdpr/delete-my-account", { password: deletePassword });
      toast.success("Account deleted. You will be logged out.");
      setShowDeleteDialog(false);
      setTimeout(() => logout(), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete account");
    } finally { setDeleting(false); }
  };

  return (
    <div className="space-y-6">
      {isOrgAdmin && <OrgSecurityCard />}

      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle>Change Password</CardTitle>
          <CardDescription>Password must be at least 8 characters with uppercase, lowercase, number, and special character</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-lg">
            <div className="space-y-2 md:col-span-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Current Password</Label>
              <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Enter current password" className="rounded-sm" data-testid="current-password-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">New Password</Label>
              <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Enter new password" className="rounded-sm" data-testid="new-password-input" />
              {newPassword && (
                <div className="space-y-1">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= strength.score ? strength.color : "bg-slate-200"}`} />
                    ))}
                  </div>
                  <p className="text-xs text-slate-500">{strength.label}</p>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Confirm New Password</Label>
              <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm new password" className="rounded-sm" data-testid="confirm-password-input" />
            </div>
          </div>
          <Button onClick={handleChangePassword} disabled={changingPw} className="rounded-sm gap-2" data-testid="change-password-btn">
            {changingPw ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
            Update Password
          </Button>
        </CardContent>
      </Card>

      {/* GDPR Data Rights */}
      <Card>
        <CardHeader>
          <CardTitle>Data Privacy (GDPR)</CardTitle>
          <CardDescription>Exercise your rights under the General Data Protection Regulation</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Download My Data */}
          <div className="flex items-start justify-between p-4 border rounded-sm">
            <div>
              <p className="font-medium text-sm">Download My Data</p>
              <p className="text-xs text-slate-500 mt-1">Export all personal data we hold about you in JSON format (GDPR Article 15 — Right of Access)</p>
            </div>
            <Button variant="outline" size="sm" className="rounded-sm gap-2 flex-shrink-0 ml-4" onClick={handleExportData} disabled={exporting} data-testid="download-my-data-btn">
              {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              Export
            </Button>
          </div>

          {/* Delete Account */}
          <div className="flex items-start justify-between p-4 border border-red-200 rounded-sm bg-red-50/50">
            <div>
              <p className="font-medium text-sm text-red-700">Delete My Account</p>
              <p className="text-xs text-red-600/70 mt-1">Permanently delete your account and anonymize all personal data (GDPR Article 17 — Right to Erasure). This action cannot be undone.</p>
            </div>
            <Button variant="destructive" size="sm" className="rounded-sm gap-2 flex-shrink-0 ml-4" onClick={() => setShowDeleteDialog(true)} data-testid="delete-account-btn">
              <Trash2 size={14} /> Delete
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Active Sessions */}
      <Card>
        <CardHeader>
          <CardTitle>Active Sessions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="p-4 border rounded-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-sm">Current Session</p>
                <p className="text-xs text-slate-500">Logged in now</p>
              </div>
              <Badge className="text-xs rounded-sm bg-emerald-100 text-emerald-700">Active</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle size={20} /> Delete Account
            </DialogTitle>
            <DialogDescription>
              This will permanently anonymize your personal data and deactivate your account. All your contracts will remain but your name will be replaced with "Deleted User". This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Confirm Your Password</Label>
            <Input type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} placeholder="Enter your password" className="rounded-sm" data-testid="delete-confirm-password" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="rounded-sm">Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteAccount} disabled={deleting || !deletePassword} className="rounded-sm gap-2" data-testid="confirm-delete-btn">
              {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
